import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./auth";
import { database } from "../firebaseConfig";
import { child, get, ref, set } from "firebase/database";

const PaymentContext = createContext();

export const usePayment = () => {
  return useContext(PaymentContext);
};

export const PaymentProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasPaid, setHasPaid] = useState(false);
  const [paypalMessage, setPaypalMessage] = useState("");

  const VERSION = process.env.REACT_APP_VERSION || "v1";

  const uid = currentUser?.auth?.uid;

  useEffect(() => {
    console.log({ VERSION });

    const checkPaymentStatus = async () => {
      setLoading(false);
      if (currentUser) {
        const dbRef = ref(database);
        const snapshot = await get(
          child(dbRef, `users/${uid}/game/${VERSION}/paid`)
        );
        const hasPaid = snapshot.val();
        setHasPaid(hasPaid);
      }
    };

    checkPaymentStatus();
  }, [currentUser]);

  const { REACT_APP_API_BASE_URL } = process.env;
  if (!REACT_APP_API_BASE_URL) {
    console.log("API_BASE_URL not set in env. Defaulting to site root.");
  }
  const apiBaseUrl = REACT_APP_API_BASE_URL || "";

  const paypalCreateOrder = async () => {
    setPaypalMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        // use the "body" param to optionally pass additional order information
        // like product ids and quantities
        body: JSON.stringify({
          cart: [
            {
              id: `kyewb${VERSION}`,
              quantity: "1",
            },
          ],
        }),
      });

      const orderData = await response.json();
      if (orderData.id) {
        return orderData.id;
      } else {
        const errorDetail = orderData?.details?.[0];
        const errorMessage = errorDetail
          ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
          : JSON.stringify(orderData);

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error(error);
      setPaypalMessage(`Could not initiate PayPal Checkout...${error}`);
    }
  };

  const paypalOnApprove = async (data, actions) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/orders/${data.orderID}/capture`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const orderData = await response.json();
      // Three cases to handle:
      //   (1) Recoverable INSTRUMENT_DECLINED -> call actions.restart()
      //   (2) Other non-recoverable errors -> Show a failure message
      //   (3) Successful transaction -> Show confirmation or thank you message

      const errorDetail = orderData?.details?.[0];

      if (errorDetail?.issue === "INSTRUMENT_DECLINED") {
        // (1) Recoverable INSTRUMENT_DECLINED -> call actions.restart()
        // recoverable state, per https://developer.paypal.com/docs/checkout/standard/customize/handle-funding-failures/
        return actions.restart();
      } else if (errorDetail) {
        // (2) Other non-recoverable errors -> Show a failure message
        throw new Error(`${errorDetail.description} (${orderData.debug_id})`);
      } else {
        // (3) Successful transaction -> Show confirmation or thank you message
        // Or go to another URL:  actions.redirect('thank_you.html');
        const transaction = orderData.purchase_units[0].payments.captures[0];
        return orderData;
      }
    } catch (error) {
      console.error(error);
      setPaypalMessage(
        `Sorry, your transaction could not be processed...${error}`
      );
    }
  };

  const handleTransaction = async (data, actions) => {
    try {
      const res = await paypalOnApprove(data, actions);
      const { uid } = currentUser.auth;
      const dbRef = ref(database);

      setPaypalMessage(
        res.status === "COMPLETED"
          ? "Payment successful!"
          : "Payment failed. Please try again."
      );
      await set(child(dbRef, `users/${uid}/game/${VERSION}/paid`), true);

      await set(
        child(dbRef, `users/${uid}/game/${VERSION}/transactions/${res.id}`),
        {
          transaction: res,
          timestamp: Date.now(),
        }
      );

      setHasPaid(true);
    } catch (error) {
      console.error("Payment failed", error);
      setPaypalMessage("Unknown error.");
    }

    try {
    } catch (error) {
      console.error("Error updating user payment status:", error);
    }
  };

  const value = {
    hasPaid,
    loading,
    paypalCreateOrder,
    paypalOnApprove,
    handleTransaction,
    paypalMessage,
  };

  return (
    <PaymentContext.Provider value={value}>
      {!loading && children}
    </PaymentContext.Provider>
  );
};
